import { useCallback, useMemo } from 'react'
import { env } from 'utils'
import { type Address, createPublicClient, erc20Abi, http } from 'viem'
import { base } from 'viem/chains'

import { useBalanceOnChain } from './useBalanceOnChain'

/**
 * Hook for fetching token balances dynamically for any token address, including ETH.
 * Provides direct balance fetching without caching for swap optimization use cases.
 * ETH addresses (0xEee...Eee and 0x000...000) are handled by delegating to fetchETHBalance.
 */
export const useTokenBalanceOnChain = (address: Address | undefined, chainId: number) => {
    const rpcUrl = useMemo(() => {
        switch (chainId) {
            case 8453:
                return env.VITE_BASE_CHAIN_RPC_URL
            default:
                return undefined
        }
    }, [chainId])

    const publicClient = useMemo(() => {
        return createPublicClient({
            chain: base,
            transport: http(rpcUrl),
        })
    }, [rpcUrl])

    const { fetchETHBalance } = useBalanceOnChain(address, chainId)

    const fetchTokenBalance = useCallback(
        async (tokenAddress: Address, blockNumber?: number): Promise<bigint> => {
            if (!address || !publicClient) {
                return 0n
            }

            // Handle ETH addresses by delegating to fetchETHBalance
            if (
                tokenAddress.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' ||
                tokenAddress.toLowerCase() === '0x0000000000000000000000000000000000000000'
            ) {
                return fetchETHBalance(blockNumber)
            }

            return publicClient.readContract({
                address: tokenAddress,
                abi: erc20Abi,
                functionName: 'balanceOf',
                args: [address],
                ...(blockNumber ? { blockNumber: BigInt(blockNumber) } : {}),
            })
        },
        [address, publicClient, fetchETHBalance],
    )

    return { fetchTokenBalance }
}
